<template>
  <div class="virtual-scroll">
    <div
      ref="list"
      :class="['virtual-scroll-list', `virtual-scroll-list--${scrollbarStyle}`]"
    >
      <div
        ref="scroller"
        :class="['scroller', { 'scroller--mobile': isMobile }]"
        :style="scrollerStyles"
      >
        <div
          v-for="item of visibleItems"
          :key="item.index"
          :class="['virtual-scroll-item', { 'virtual-scroll-item--mobile': isMobile }]"
          :style="item.css"
          @click="onClick(item)"
          @dblclick="onClick(item)"
        >
          <slot
            v-if="item && items[item.index]"
            v-bind="{ item: items[item.index], index: item.index }"
          >
            {{ item[keyProp] }}
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TItem">
import { debounce } from 'lodash-es'
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
  useTemplateRef,
  useResizeObserver,
} from '@/composition/vue/compositionApi'
import { useBreakpoints } from '@/composition/breakpoints'
import { TmComponentError } from '@/core/error/TmComponentError'
import type { ScrollbarStyle } from '@/components/shared/types'
import type { VScrollComponent } from '@/components/domain/chats/types'
import type { Nullable, StylesheetSizeProp } from '@/types'

interface IRowContext {
  index: number
  offsetY: number
  rowHeight: number
  css: Record<string, string>
}

type TRowHeight = number | null | undefined

const props = withDefaults(
  defineProps<{
    items: TItem[]
    keyProp?: string
    minRowHeight?: number
    rowHeight?: TRowHeight
    itemGap?: number
    withExtraPages?: boolean
    scrollToDown?: boolean
    scrollbarStyle?: ScrollbarStyle
  }>(),
  {
    keyProp: 'id',
    itemGap: 0,
    withExtraPages: false,
    scrollToDown: false,
    scrollbarStyle: 'thumb-with-track',
  },
)

const emit = defineEmits<{
  click: [{ row: TItem; index: number }]
  dblclick: [{ row: TItem; index: number }]
  page: [dir: 'up' | 'down']
}>()

const rList = useTemplateRef('list')
const rScroller = useTemplateRef('scroller')

const bodyHeight = ref(0)
const visibleItems = ref<Array<IRowContext>>([])
const rowContexts = ref<Array<IRowContext>>([])

const { isMobile } = useBreakpoints()

const itemsHeightCache: Map<string | number, number> = new Map()
const pagesEmit = new Set()

let itemsResizeObserver: ResizeObserver | undefined
const elementToRowIndex = new Map<Element, number>()
let observeScheduled = false

let offsetY = 0
let lastRowOffsetY = 0
let stopTick = false
let firstVisible = -1
let lastVisible = -1
let clickCounter = 0
let clickTimerId: ReturnType<typeof setTimeout>
let rafId: ReturnType<typeof requestAnimationFrame> = 0
let forceNextUpdate = false
let shouldScrollToLast = false
let scrollTop = 0
let scrollDirUp = false
let lastMaxScrollTopValue = 0

const rowCount = computed(() => {
  return props.items.length ?? 0
})

const approximateRowHeight = computed(() => {
  if (isRowHeightFixed.value) {
    return (props.rowHeight as number) + props.itemGap
  }
  return props.minRowHeight ?? 10
})

const isRowHeightFixed = computed(() => {
  return typeof props.rowHeight === 'number' && !Number.isNaN(props.rowHeight) && props.rowHeight > 0
})

const scrollHeight = computed(() => {
  if (!bodyHeight.value) {
    // it is need to bodyHeight changes affect to this computed value
    return 0
  }
  if (isRowHeightFixed.value) {
    return (props.rowHeight as number) * rowCount.value
  }
  let result = 0
  if (rowCount.value && rowContexts.value?.length) {
    const rowContext = rowContexts.value[rowCount.value - 1]
    if (rowContext?.rowHeight) {
      result = rowContext.offsetY + rowContext.rowHeight
    }
  }
  if (!result) {
    result = approximateRowHeight.value * rowCount.value
  }
  result = Math.max(result, lastRowOffsetY - props.itemGap)
  return result
})

const scrollerStyles = computed(() => {
  return {
    height: `${scrollHeight.value || approximateRowHeight.value * rowCount.value}px` satisfies StylesheetSizeProp,
    width: '100%' satisfies StylesheetSizeProp,
  }
})

useResizeObserver(rList, (entries) => {
  if (!rList.value) {
    return
  }
  if (entries.length && entries[0].contentRect) {
    bodyHeight.value = entries[0].contentRect.height
  } else {
    bodyHeight.value = rList.value.clientHeight
  }
  scheduleForceUpdate()
})

onMounted(() => {
  if (!rList.value) {
    throw new TmComponentError('Root element not found')
  }
  const dims = rList.value.getBoundingClientRect()
  bodyHeight.value = dims.height

  rList.value.addEventListener('scroll', scheduleUpdate, { passive: true })

  // Process initial frame: offsetY is -1 after onItemsChanged, so it will trigger updateVisibleRows
  processFrame()

  scheduleObserveVisibleItems()
})

onBeforeUnmount(() => {
  stopTick = true

  rList.value?.removeEventListener('scroll', scheduleUpdate)

  clearTimeout(clickTimerId)
  debouncedPageEventEmit.cancel()
  cancelAnimationFrame(rafId)
  itemsHeightCache.clear()

  itemsResizeObserver?.disconnect()
})

const debouncedPageEventEmit = debounce((el: HTMLElement) => {
  if (shouldScrollToLast || (props.scrollToDown && !scrollDirUp) || (!props.scrollToDown && scrollDirUp)) {
    return
  }
  const maxScrollTopValue = el.scrollHeight - el.clientHeight
  if (pagesEmit.has(maxScrollTopValue) || maxScrollTopValue < lastMaxScrollTopValue) {
    lastMaxScrollTopValue = maxScrollTopValue
    return
  }
  const limitScrollPos = scrollDirUp ? maxScrollTopValue * 0.3 : maxScrollTopValue / 2
  if ((scrollDirUp && scrollTop < limitScrollPos) || (!scrollDirUp && scrollTop > limitScrollPos)) {
    emit('page', scrollDirUp ? 'up' : 'down')
    pagesEmit.add(maxScrollTopValue)
  }
  lastMaxScrollTopValue = maxScrollTopValue
}, 35)

/** Schedule a frame to process scroll position changes. */
const scheduleUpdate = () => {
  if (!rafId) {
    rafId = requestAnimationFrame(processFrame)
  }
}

/** Schedule a forced frame (e.g. after container resize) that bypasses the scroll-position-unchanged check. */
const scheduleForceUpdate = () => {
  forceNextUpdate = true
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(processFrame)
}

const processFrame = () => {
  rafId = 0
  if (stopTick || !rList.value) {
    return
  }
  if (shouldScrollToLast && offsetY !== rList.value.scrollHeight - rList.value.clientHeight) {
    scrollToEnd()
  }
  scrollTop = rList.value.scrollTop

  const force = forceNextUpdate
  forceNextUpdate = false

  if (!force && offsetY === scrollTop) {
    return
  }
  scrollDirUp = offsetY > scrollTop
  offsetY = scrollTop
  if (props.scrollToDown) {
    shouldScrollToLast = scrollTop === rList.value.scrollHeight - rList.value.clientHeight
  }
  debouncedPageEventEmit(rList.value)
  updateVisibleRows()
}

const updateVisibleRows = (force = false) => {
  const { first, last } = getIndexes()
  if (last < first) {
    return
  }
  if (!force && firstVisible === first && lastVisible === last) {
    return
  }
  let before = first
  let after = last
  let rest = 0
  const pageSize = last - first
  if (props.withExtraPages && pageSize * 3 <= rowCount.value) {
    const extraPageSize = (pageSize * 3 - pageSize) / 2
    before = Math.max(0, first - extraPageSize)
    after = Math.min(last + extraPageSize, rowCount.value)
    if (before > first - extraPageSize) {
      rest = before - (first - extraPageSize)
      after = Math.min(rowCount.value, after + rest)
    } else if (after < last + extraPageSize) {
      rest = last + extraPageSize - after
      before = Math.max(0, before - rest)
    }
  }
  firstVisible = first
  lastVisible = last
  let rowIndex = before
  let idx = 0
  visibleItems.value.length = Math.max(after - before, 1)
  const lastIndex = Math.min(after, rowCount.value - 1)
  while (rowIndex <= lastIndex) {
    visibleItems.value[idx] = rowContexts.value[rowIndex]
    idx += 1
    rowIndex += 1
  }

  scheduleObserveVisibleItems()
}

/**
 * Find the last row index whose top edge is at or above the given scroll position.
 * Uses binary search over rowContexts (sorted by offsetY).
 * Returns -1 if no row satisfies the condition.
 */
const findLastRowAbove = (scrollPos: number): number => {
  const contexts = rowContexts.value
  const tolerance = 10
  let lo = 0
  let hi = contexts.length - 1
  let result = -1

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (contexts[mid].offsetY - tolerance <= scrollPos) {
      result = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  return result
}

const getIndexes = (): { first: number; last: number } => {
  if (!rowContexts.value.length) {
    return { first: -1, last: -2 }
  }

  const viewportTop = offsetY
  const viewportBottom = offsetY + bodyHeight.value

  // Last visible row: the last row that starts before the viewport bottom
  let last = findLastRowAbove(viewportBottom)
  if (last < 0) {
    last = rowContexts.value.length - 1
  }

  // First visible row: the last row that starts before the viewport top, minus one for safety
  const first = Math.max(findLastRowAbove(viewportTop) - 1, 0)

  return { first, last }
}

const getUid = (index: number): string | number | null => {
  const entity = props.items[index]
  return entity ? (entity[props.keyProp] as string | number) : null
}

const updateRowOffsetsCache = (index: number, all?: boolean) => {
  let context = rowContexts.value[index]
  if (!context) {
    return
  }
  let offsetVert = context.offsetY
  let height = context.rowHeight
  index += 1
  context = rowContexts.value[index]
  const last = all ? rowContexts.value.length : lastVisible + 1
  while (context && index < last) {
    offsetVert += height
    context.offsetY = offsetVert
    context.css.transform = `translateY(${offsetVert}px)`
    height = context.rowHeight
    index += 1
    context = rowContexts.value[index]
  }
  lastRowOffsetY = offsetVert
}

const scheduleObserveVisibleItems = () => {
  if (observeScheduled) return
  observeScheduled = true
  nextTick(() => {
    observeScheduled = false
    observeVisibleItems()
  })
}

const observeVisibleItems = () => {
  if (isRowHeightFixed.value || !rScroller.value) {
    return
  }

  if (typeof itemsResizeObserver === 'undefined') {
    itemsResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (isRowHeightFixed.value) return

      let minChangedIndex = Number.POSITIVE_INFINITY

      for (const entry of entries) {
        const rowIndex = elementToRowIndex.get(entry.target as Element)
        if (typeof rowIndex !== 'number') continue

        const rc = rowContexts.value[rowIndex]
        if (!rc) continue

        const h = (entry.contentRect?.height ?? 0) + props.itemGap
        if (rc.rowHeight === h) continue

        rc.rowHeight = h
        if (rc.index === 0) {
          rc.offsetY = 0
        }

        const id = getUid(rc.index)
        if (id) {
          itemsHeightCache.set(id, h)
        }

        minChangedIndex = Math.min(minChangedIndex, rc.index)
      }

      if (minChangedIndex < Infinity) {
        updateRowOffsetsCache(minChangedIndex, true)
      }
    })
  }

  const oldElements = new Set(elementToRowIndex.keys())
  const newEntries = new Map<Element, number>()

  const children = rScroller.value.children
  for (let i = 0; i < children.length; i++) {
    const el = children[i]
    const item = visibleItems.value[i]
    if (item) {
      newEntries.set(el, item.index)
      if (oldElements.has(el)) {
        oldElements.delete(el)
      } else {
        itemsResizeObserver.observe(el)
      }
    }
  }

  // Unobserve elements that are no longer visible
  for (const el of oldElements) {
    itemsResizeObserver.unobserve(el)
  }

  elementToRowIndex.clear()
  for (const [el, idx] of newEntries) {
    elementToRowIndex.set(el, idx)
  }
}

const onClick = (rc: IRowContext) => {
  clickCounter += 1
  if (clickCounter === 1) {
    clickTimerId = setTimeout(() => {
      emit('click', { row: props.items[rc.index], index: rc.index })
      clickCounter = 0
    }, 200)
  } else {
    clearTimeout(clickTimerId)
    emit('dblclick', { row: props.items[rc.index], index: rc.index })
    clickCounter = 0
  }
}

const getScrollElement = () => {
  if (!rList.value) {
    throw new TmComponentError('No list component')
  }

  return rList.value
}

const scrollToEnd = () => {
  if (rList.value) {
    rList.value.scrollTop = rList.value.scrollHeight - rList.value.clientHeight
  }
}

const scrollTo = (index: number) => {
  const rc = rowContexts.value[index]
  if (!rList.value || !rc) {
    return
  }

  const currentScrollTop = rList.value.scrollTop
  const viewportHeight = rList.value.clientHeight
  const itemTop = rc.offsetY
  const itemBottom = itemTop + rc.rowHeight

  // If item is above visible area - scroll up
  if (itemTop < currentScrollTop) {
    rList.value.scrollTop = itemTop
  }
  // If item is below visible area - scroll down
  else if (itemBottom > currentScrollTop + viewportHeight) {
    rList.value.scrollTop = itemBottom - viewportHeight
  }
}

const onItemsChanged = (newVal: TItem[], oldVal?: TItem[]) => {
  rowContexts.value.length = newVal.length
  let updateOffsets = false
  newVal.forEach((item, i) => {
    const offsetVert = i === 0 ? 0 : rowContexts.value[i - 1].offsetY + rowContexts.value[i - 1].rowHeight

    let height: Nullable<number> = 0
    if (item) {
      const id = item[props.keyProp] as string | number
      height = itemsHeightCache.get(id) ?? null
    }

    if (!rowContexts.value[i]) {
      const styles: Record<string, string> = { transform: `translateY(${offsetVert}px)` }
      rowContexts.value[i] = {
        index: i,
        offsetY: offsetVert,
        rowHeight: item ? (height ?? approximateRowHeight.value) : 0,
        css: styles,
      }
    } else {
      rowContexts.value[i].offsetY = offsetVert
      rowContexts.value[i].css.transform = `translateY(${offsetVert}px)`
      if (height || !item) {
        rowContexts.value[i].rowHeight = height ?? 0
        updateOffsets = true
      }
    }
  })
  if (!updateOffsets && oldVal) {
    updateOffsets = newVal.length <= oldVal.length
  }
  if (updateOffsets) {
    updateRowOffsetsCache(0, true)
  }
  offsetY = -1
  if (newVal?.length === oldVal?.length) {
    firstVisible = -1
  }
  if (oldVal && (!newVal || newVal.length < oldVal.length)) {
    pagesEmit.clear()
  }

  scheduleForceUpdate()
}

watch(() => props.items, onItemsChanged, { immediate: true })

watch(scrollHeight, (newVal, oldVal) => {
  if (shouldScrollToLast) {
    scrollToEnd()
    return
  }
  if (!scrollDirUp || !rList.value || !oldVal || rList.value.scrollTop + newVal - oldVal < 0) {
    return
  }
  nextTick(() => {
    if (!rList.value) {
      return
    }
    scrollTop += newVal - oldVal
    rList.value.scrollTop = scrollTop
    offsetY = scrollTop
    updateVisibleRows()
  })
})

defineExpose({
  getScrollElement,
  scrollTo,
  scrollToEnd,
} satisfies VScrollComponent)
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.virtual-scroll {
  display: flex;
  flex-flow: column nowrap;
  position: relative;
  height: 100%;
  will-change: scroll-position;
}

.virtual-scroll-list {
  @include scrollbar-variants();

  width: 100%;
  overflow-y: auto;
  overflow-anchor: auto;
  overscroll-behavior-y: contain;
  contain: content;
}

.scroller {
  position: relative;
  margin-bottom: var(--tm-virtual-scroll-scroller-bottom-offset, 0);

  &.scroller--mobile {
    scroll-snap-type: y mandatory;
  }
}

.virtual-scroll-item {
  position: absolute;
  width: 100%;
  will-change: transform;
  backface-visibility: inherit;

  &.virtual-scroll-item--mobile {
    scroll-snap-align: start;
  }
}
</style>
